import { Scrollable } from 'components/atoms';
import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  CategoryProductRow,
  DesktopCategoryFilter,
  formatCategoryName,
  MobileCategorySidebar,
  MobileCategoryFilter
} from './CategoryComponents';
import HeaderVendor from './header-vendor';
import { CategorySection } from './style';
import useProductsByVendorsCategoriesHook from './useProductsByVendorsCategoriesHook';

interface Product {
  productId: number;
  productName: string;
  price: number;
  quantityAvailable: number;
  minimumOrderQuantity: number;
  bulkOrderPrice: number;
  barCode: string;
  productImage1: string;
  subCategory: string;
  status?: string;
  description?: string;
}

const ProductsByVendor = () => {
  const {
    fetchMoreProductsByVendors,
    productsByVendorsData,
    productsByVendorsisFetching,
    productsByVendorsHasNext,
    seller,
    subCategories,
    isInitialLoading,
    isFetchingNextPage
  } = useProductsByVendorsCategoriesHook() as {
    fetchMoreProductsByVendors: () => void;
    productsByVendorsData: any;
    productsByVendorsisFetching: boolean;
    productsByVendorsHasNext: boolean;
    seller: any;
    subCategories: string[];
    isInitialLoading?: boolean;
    isFetchingNextPage?: boolean;
  };

  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [showSidebar, setShowSidebar] = useState(false);

  // Function to handle search query change
  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
  };

  // Function to handle back button click
  const handleBack = () => {
    const params = new URLSearchParams(location.search);
    params.delete('vendor');
    navigate({ search: params.toString() });
  };

  // Function to handle category click navigation
  const handleCategoryClick = (categoryEnum: string) => {
    const params = new URLSearchParams(location.search);
    const vendorParam = params.get('vendor');
    const categoryParam = params.get('category');

    if (vendorParam) {
      const pathSegments = location.pathname.split('/');
      const currentCategoryName = pathSegments[pathSegments.length - 1];
      const currentPath = location.pathname;
      const basePath = currentPath.split('/').slice(0, -1).join('/');

      navigate(`${basePath}/${categoryParam}/${vendorParam}/${categoryEnum}`, {
        state: {
          seller: seller,
          currentCategory: currentCategoryName
        }
      });
    }
  };

  // Function to handle "View All" for a category
  const handleViewAll = (categoryEnum: string) => {
    handleCategoryClick(categoryEnum);
  };

  // Function to handle category selection in sidebar
  const handleCategorySelect = (categoryEnum: string | null) => {
    setSelectedCategory(categoryEnum);
  };

  // Get all products using useMemo for better performance
  const allProducts = useMemo(() => {
    if (!productsByVendorsData?.pages) return [];

    console.log('All pages:', productsByVendorsData.pages);

    // Flatten all pages and extract products
    return productsByVendorsData.pages.flatMap((page: any) => {
      console.log('Processing page:', page);

      // Your API returns data in the 'data' property
      if (page?.data && Array.isArray(page.data)) {
        console.log('Found products in page.data:', page.data.length);
        return page.data;
      }

      console.log('No products found in page');
      return [];
    });
  }, [productsByVendorsData?.pages]);

  // Group products by category with better memoization
  const groupedProducts = useMemo(() => {
    console.log('Grouping products. Total products:', allProducts.length);
    console.log('Available subcategories:', subCategories);

    const groups: { [key: string]: { categoryEnum: string; products: Product[] } } = {};

    // Initialize groups for each subcategory
    subCategories?.forEach(categoryEnum => {
      groups[categoryEnum] = {
        categoryEnum,
        products: []
      };
    });

    // Group products by their subcategory
    allProducts.forEach((product: Product) => {
      if (product.subCategory) {
        if (groups[product.subCategory]) {
          // Apply search filter
          if (searchQuery.trim()) {
            const searchLower = searchQuery.toLowerCase();
            const matchesSearch =
              product.productName?.toLowerCase().includes(searchLower) ||
              product.barCode?.toLowerCase().includes(searchLower) ||
              product.description?.toLowerCase().includes(searchLower);

            if (matchesSearch) {
              groups[product.subCategory].products.push(product);
            }
          } else {
            groups[product.subCategory].products.push(product);
          }
        } else {
          console.log('Product has unknown subcategory:', product.subCategory);
        }
      }
    });

    // Define the fixed order for categories
    const categoryOrder = ['PROVISIONS', 'DRINKS', 'COSMETICS', 'TOILETRIES'];

    // Filter out empty categories and sort by the fixed order
    const result = Object.values(groups)
      .filter(group => group.products.length > 0)
      .sort((a, b) => {
        const aIndex = categoryOrder.indexOf(a.categoryEnum);
        const bIndex = categoryOrder.indexOf(b.categoryEnum);

        // If both categories are in the order array, sort by their position
        if (aIndex !== -1 && bIndex !== -1) {
          return aIndex - bIndex;
        }

        // If only one is in the order array, prioritize it
        if (aIndex !== -1) return -1;
        if (bIndex !== -1) return 1;

        // If neither is in the order array, maintain alphabetical order
        return a.categoryEnum.localeCompare(b.categoryEnum);
      });

    console.log(
      'Grouped products result:',
      result.map(g => ({ category: g.categoryEnum, count: g.products.length }))
    );
    return result;
  }, [allProducts, subCategories, searchQuery]);

  // Filter products based on selected category
  const filteredGroupedProducts = useMemo(() => {
    if (selectedCategory) {
      return groupedProducts.filter(group => group.categoryEnum === selectedCategory);
    }
    return groupedProducts;
  }, [groupedProducts, selectedCategory]);

  // Check if there are no products at all
  const noProducts = allProducts.length === 0;

  const sellerData: any = seller;

  const emptyMessage = noProducts
    ? 'Unfortunately, no products are currently offered by this vendor'
    : searchQuery.trim() && groupedProducts.length === 0
    ? `No products found for "${searchQuery}"`
    : '';

  return (
    <div className="min-h-screen bg-gray-50">
      <HeaderVendor
        title={sellerData?.businessName || 'Loading...'}
        description={sellerData?.businessDescription || ''}
        vendorBannerSrc={sellerData?.seller_logo}
        handleBack={handleBack}
        seller={sellerData}
        isLoading={isInitialLoading || (!seller && productsByVendorsisFetching)}
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
      />

      {/* Show content only when not in initial loading state */}
      {!(isInitialLoading || (!seller && productsByVendorsisFetching)) && (
        <div className="relative">
          {/* Mobile Sidebar Toggle */}
          {/* <div className="lg:hidden fixed top-20 left-4 z-40">
            <button
              onClick={() => setShowSidebar(!showSidebar)}
              className="p-2 bg-white border border-gray-300 rounded-lg shadow-lg">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div> */}

          {/* Mobile Sidebar Overlay */}
          {showSidebar && (
            <div
              className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
              onClick={() => setShowSidebar(false)}
            />
          )}

          {/* Mobile Sidebar */}
          <div
            className={`${
              showSidebar ? 'translate-x-0' : '-translate-x-full'
            } lg:hidden fixed inset-y-0 left-0 z-40 transition-transform duration-300 ease-in-out`}>
            <MobileCategorySidebar
              allCategories={subCategories || []}
              groupedProducts={groupedProducts}
              selectedCategory={selectedCategory}
              onCategorySelect={handleCategorySelect}
              onViewAll={handleViewAll}
            />
          </div>

          {/* Main Content - Full width on desktop */}
          <div className="w-full">
            <Scrollable
              onScrollBottom={fetchMoreProductsByVendors}
              callWhen={productsByVendorsHasNext && !isFetchingNextPage}>
              <CategorySection className="px-4 lg:px-6">
                {noProducts || filteredGroupedProducts.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-gray-600 text-lg">{emptyMessage}</p>
                  </div>
                ) : (
                  <div className="space-y-8">
                    {/* Category Filter Info */}
                    {selectedCategory && (
                      <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-600">Showing products in:</span>
                          <span className="font-medium text-blue-900">{formatCategoryName(selectedCategory)}</span>
                        </div>
                        <button
                          onClick={() => setSelectedCategory(null)}
                          className="text-sm text-blue-600 hover:text-blue-800 underline">
                          Clear filter
                        </button>
                      </div>
                    )}

                    {filteredGroupedProducts.map(({ categoryEnum, products }) => (
                      <CategoryProductRow
                        key={categoryEnum}
                        categoryEnum={categoryEnum}
                        products={products}
                        seller={sellerData}
                        onCategoryClick={handleCategoryClick}
                        onViewAll={handleViewAll}
                        showViewAll={!selectedCategory}
                      />
                    ))}

                    {/* Loading indicator for infinite scroll */}
                    {/* {(isFetchingNextPage || productsByVendorsisFetching) && (
                      <div className="text-center py-8">
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        <p className="text-gray-500 mt-2">
                          {isFetchingNextPage ? 'Loading more products...' : 'Loading...'}
                        </p>
                      </div>
                    )} */}
                  </div>
                )}
              </CategorySection>
            </Scrollable>

            {/* Desktop Bottom Category Filter */}
            <div className="hidden lg:block">
              <DesktopCategoryFilter
                allCategories={subCategories || []}
                groupedProducts={groupedProducts}
                selectedCategory={selectedCategory}
                onCategorySelect={handleCategorySelect}
                onViewAll={handleViewAll}
              />
            </div>
            {/* Mobile Browse by Category Filter */}
            <div className="block lg:hidden">
              <MobileCategoryFilter
                allCategories={subCategories || []}
                groupedProducts={groupedProducts}
                selectedCategory={selectedCategory}
                onCategorySelect={handleCategorySelect}
                onViewAll={handleViewAll}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductsByVendor;
